import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { calendarRequest, calendarQuery, DAYS, validateWeeklyHours } from '../../services/calendarService.js';

export default function AvailabilityDialog({ open, onClose, onChanged, doctors, teamId, userId, isAdmin }) {
  const eligible = doctors.filter(doctor => isAdmin || String(doctor.user_id) === String(userId));
  const [locations, setLocations] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [days, setDays] = useState({});
  const [version, setVersion] = useState(null);
  const [timezone, setTimezone] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [reload, setReload] = useState(0);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setDoctorId(String(eligible[0]?.id || '')); setLocationId('');
      setTab(0); setError(''); setStart(''); setEnd(''); setReason('');
    }
    // Selections are initialized when opening, not overwritten by background refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId]);
  useEffect(() => {
    setLocations([]); setLocationId(''); setLoaded(false);
    if (!open || !doctorId || !teamId) return undefined;
    const controller = new AbortController();
    setBusy(true); setError('');
    calendarRequest(`availability?${calendarQuery({ doctor_id: doctorId, team_id: teamId })}`, { signal: controller.signal })
      .then(data => { if (!controller.signal.aborted) { setLocations(data.locations || []); setLocationId(String(data.locations?.[0]?.location_id || '')); } })
      .catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [open, doctorId, teamId]);
  useEffect(() => {
    setLoaded(false);
    if (!open || !doctorId || !locationId || !teamId) return undefined;
    const controller = new AbortController();
    setBusy(true); setError('');
    const query = calendarQuery({ doctor_id: doctorId, team_id: teamId, location_id: locationId });
    Promise.all([
      calendarRequest(`availability?${query}`, { signal: controller.signal }),
      calendarRequest(`availability/exceptions?${query}`, { signal: controller.signal }),
    ]).then(([schedule, timeOff]) => {
      if (controller.signal.aborted) return;
      setDays(schedule.selected_days || {}); setVersion(schedule.version); setTimezone(schedule.timezone || '');
      setExceptions(timeOff.exceptions || []); setLoaded(true);
    }).catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [open, doctorId, locationId, teamId, reload]);
  const scope = { doctor_id: doctorId, team_id: teamId, location_id: locationId };
  const run = async task => {
    setBusy(true); setError('');
    try { await task(); onChanged(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const save = () => {
    const invalid = validateWeeklyHours(days);
    if (invalid) { setError(invalid); return; }
    run(async () => {
      await calendarRequest('availability', { method: 'PUT', body: { ...scope, selected_days: days, version } });
      onClose();
    });
  };
  const addTimeOff = () => {
    if (!start || !end || new Date(start) >= new Date(end)) { setError('Choose a valid start and later end for time off.'); return; }
    run(async () => {
      await calendarRequest('availability/exceptions', { method: 'POST', body: { ...scope, starts_at: new Date(start).toISOString(), ends_at: new Date(end).toISOString(), kind: 'unavailable', reason } });
      setStart(''); setEnd(''); setReason(''); setReload(value => value + 1);
    });
  };
  return <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{isAdmin ? 'Manage availability' : 'My availability'}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {!eligible.length && <Alert severity="info">There is no therapist profile available in this organization.</Alert>}
      {!locations.length && <Alert severity="info">Add this therapist’s clinic locations in their Profile first.</Alert>}
      <TextField select label="Therapist" value={doctorId} disabled={busy || !isAdmin} onChange={event => setDoctorId(event.target.value)}>{eligible.map(doctor => <MenuItem key={doctor.id} value={String(doctor.id)}>{doctor.name}</MenuItem>)}</TextField>
      <TextField select label="Clinic location" value={locationId} disabled={busy} onChange={event => setLocationId(event.target.value)}>{locations.map(item => <MenuItem key={item.location_id} value={String(item.location_id)}>{item.name || item.address}</MenuItem>)}</TextField>
      {busy && <CircularProgress size={24} aria-label="Loading availability" />}
      {loaded && <>
        <Typography variant="body2" color="text.secondary">Working hours use {timezone || 'the clinic time zone'}. Existing appointments are kept when availability changes.</Typography>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}><Tab label="Weekly hours" /><Tab label="Time off" /></Tabs>
        {tab === 0 ? DAYS.map(day => <Stack key={day} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1}>
          <FormControlLabel sx={{ minWidth: 145 }} control={<Checkbox disabled={busy} checked={Boolean(days[day])} onChange={event => setDays(previous => { const next = { ...previous }; if (event.target.checked) next[day] = { start: '09:00', end: '18:00' }; else delete next[day]; return next; })} />} label={day} />
          {days[day] && ['start', 'end'].map(field => <TextField key={field} type="time" label={`${day} ${field}`} size="small" value={days[day][field] || ''} InputLabelProps={{ shrink: true }} disabled={busy} onChange={event => setDays(previous => ({ ...previous, [day]: { ...previous[day], [field]: event.target.value } }))} />)}
        </Stack>) : <>
          <Typography variant="body2">Time off blocks this therapist across all clinic locations. Enter dates in your device time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).</Typography>
          {exceptions.map(item => <Box key={item.id} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
            <Typography variant="body2">{new Date(item.starts_at).toLocaleString()} – {new Date(item.ends_at).toLocaleString()}</Typography>
            <Typography variant="body2">{item.reason || 'Unavailable'}</Typography>
            <Button disabled={busy} color="error" onClick={() => run(async () => { await calendarRequest(`availability/exceptions/${encodeURIComponent(item.id)}?${calendarQuery(scope)}`, { method: 'DELETE' }); setReload(value => value + 1); })}>Remove time off</Button>
          </Box>)}
          <TextField type="datetime-local" label="Time off starts" InputLabelProps={{ shrink: true }} value={start} disabled={busy} onChange={event => setStart(event.target.value)} />
          <TextField type="datetime-local" label="Time off ends" InputLabelProps={{ shrink: true }} value={end} disabled={busy} onChange={event => setEnd(event.target.value)} />
          <TextField label="Reason (optional)" value={reason} inputProps={{ maxLength: 120 }} disabled={busy} onChange={event => setReason(event.target.value)} />
          <Button variant="outlined" disabled={busy} onClick={addTimeOff}>Add time off</Button>
        </>}
      </>}
    </Stack></DialogContent>
    <DialogActions><Button disabled={busy} onClick={onClose}>Close</Button>{tab === 0 && <Button variant="contained" disabled={busy || !loaded} onClick={save}>Save working hours</Button>}</DialogActions>
  </Dialog>;
}
